import writeXlsxFile from 'write-excel-file/browser'
import { readSheet as readXlsxFile } from 'read-excel-file/browser'

// ─── ID flattening ───────────────────────────────────────────────────────────

function isIdObject(v: unknown): v is Record<string, unknown> & { id: unknown } {
  return v !== null && typeof v === 'object' && !Array.isArray(v) && 'id' in (v as object)
}

/**
 * Recursively flatten objects that have an `id` field into their id value.
 * { key: { id: "123", name: "Foo" } } → { key: "123" }
 * { key: [{ id: "123" }] }            → { key: ["123"] }
 */
export function flattenIds(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (Array.isArray(value)) {
      result[key] = value.map((item) => (isIdObject(item) ? item.id : item))
    } else if (isIdObject(value)) {
      result[key] = value.id
    } else {
      result[key] = value
    }
  }
  return result
}

/**
 * Wrap string values (or string arrays) back into { id: string } objects
 * for the fields listed in `idFields`.
 * { key: "123" }       → { key: { id: "123" } }
 * { key: ["123"] }     → { key: [{ id: "123" }] }
 */
export function unflattenIds(
  obj: Record<string, unknown>,
  idFields: string[]
): Record<string, unknown> {
  const result = { ...obj }
  for (const field of idFields) {
    const val = result[field]
    if (typeof val === 'string' && val.trim()) {
      result[field] = { id: val.trim() }
    } else if (Array.isArray(val)) {
      result[field] = val.map((v) =>
        typeof v === 'string' && v.trim() ? { id: v.trim() } : v
      )
    }
  }
  return result
}

// ─── Field transformers ───────────────────────────────────────────────────────

export interface FieldTransformer {
  /** Convert the native value to a display string for the file cell. */
  serialize: (value: unknown) => string
  /** Parse the cell string back to the native value. */
  deserialize: (cell: string) => unknown
  /**
   * Return an error message if the cell string is invalid, or null if it's fine.
   * Called before the confirm modal; a non-null result blocks the upload.
   */
  validate?: (cell: string) => string | null
}

/** Ready-made transformer for ad-unit dimensions: [[980,240],[300,200]] ↔ "980x240, 300x200" */
export const dimensionsTransformer: FieldTransformer = {
  serialize(value) {
    if (!Array.isArray(value) || value.length === 0) return ''
    return (value as [number, number][]).map(([w, h]) => `${w}x${h}`).join(', ')
  },
  deserialize(cell) {
    const s = cell.trim()
    if (!s) return []
    return s.split(',').map((part) => {
      const [w, h] = part.trim().split('x').map(Number)
      return [w, h] as [number, number]
    })
  },
  validate(cell) {
    const s = cell.trim()
    if (!s) return null
    const parts = s.split(',').map((p) => p.trim())
    const bad = parts.find((p) => !/^\d+x\d+$/.test(p))
    if (bad) return `Invalid dimension "${bad}" — expected format: 980x240`
    return null
  },
}

// ─── Cell serialisation ───────────────────────────────────────────────────────

function serializeCell(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (Array.isArray(v) || (typeof v === 'object')) return JSON.stringify(v)
  return String(v)
}

function deserializeCell(v: string): unknown {
  const s = v.trim()
  if (s === '') return ''
  if (s === 'true') return true
  if (s === 'false') return false
  if (s[0] === '[' || s[0] === '{') {
    try { return JSON.parse(s) } catch { /* fall through */ }
  }
  const n = Number(s)
  if (!isNaN(n) && s !== '') return n
  return s
}

// ─── CSV ─────────────────────────────────────────────────────────────────────

function escapeCSVCell(cell: string): string {
  if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
    return `"${cell.replace(/"/g, '""')}"`
  }
  return cell
}

function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++ }
      else inQuotes = !inQuotes
    } else if (c === ',' && !inQuotes) {
      result.push(current); current = ''
    } else {
      current += c
    }
  }
  result.push(current)
  return result
}

function applyTransformersToRow(
  row: Record<string, unknown>,
  transformers: Record<string, FieldTransformer>
): Record<string, string | unknown> {
  const out = { ...row }
  for (const [field, tx] of Object.entries(transformers)) {
    if (field in out) out[field] = tx.serialize(out[field])
  }
  return out
}

function reverseTransformersOnRow(
  row: Record<string, unknown>,
  transformers: Record<string, FieldTransformer>
): Record<string, unknown> {
  const out = { ...row }
  for (const [field, tx] of Object.entries(transformers)) {
    if (field in out) out[field] = tx.deserialize(String(out[field] ?? ''))
  }
  return out
}

export function exportToCSV(
  data: Record<string, unknown>[],
  filename: string,
  transformers: Record<string, FieldTransformer> = {},
  templateFields?: string[],
  templateExample?: Record<string, string>
): void {
  if (!data.length) {
    if (!templateFields?.length) return
    const headerRow = templateFields.join(',')
    const exampleRow = templateExample
      ? templateFields.map((h) => escapeCSVCell(templateExample[h] ?? '')).join(',')
      : null
    const csv = exampleRow ? `${headerRow}\n${exampleRow}` : headerRow
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${filename}-template.csv`)
    return
  }
  const prepared = data.map((row) => applyTransformersToRow(row, transformers))
  const headers = Object.keys(prepared[0])
  const rows = prepared.map((row) =>
    headers.map((h) => escapeCSVCell(serializeCell(row[h]))).join(',')
  )
  const csv = [headers.join(','), ...rows].join('\n')
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${filename}.csv`)
}

export async function parseCSV(
  file: File,
  transformers: Record<string, FieldTransformer> = {}
): Promise<Record<string, unknown>[]> {
  const text = await file.text()
  const lines = text.split('\n').filter((l) => l.trim())
  if (lines.length < 2) return []
  const headers = parseCSVLine(lines[0])
  const rows = lines.slice(1).map((line) => {
    const values = parseCSVLine(line)
    const obj: Record<string, unknown> = {}
    headers.forEach((h, i) => { obj[h] = deserializeCell(values[i] ?? '') })
    return reverseTransformersOnRow(obj, transformers)
  })
  return rows
}

// ─── Excel ───────────────────────────────────────────────────────────────────

export async function exportToExcel(
  data: Record<string, unknown>[],
  filename: string,
  transformers: Record<string, FieldTransformer> = {},
  templateFields?: string[],
  templateExample?: Record<string, string>,
  /** A bold closing row under the data, keyed like the data rows; missing keys stay blank. */
  totals?: Record<string, unknown>
): Promise<void> {
  if (!data.length) {
    if (!templateFields?.length) return
    const rows: { value: string; fontWeight?: 'bold' }[][] = [
      templateFields.map((h) => ({ value: h, fontWeight: 'bold' as const })),
    ]
    if (templateExample) {
      rows.push(templateFields.map((h) => ({ value: templateExample[h] ?? '' })))
    }
    const blob = await writeXlsxFile(rows).toBlob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${filename}-template.xlsx`
    a.click()
    URL.revokeObjectURL(url)
    return
  }
  const serialized = data.map((row) => {
    const prepared = applyTransformersToRow(row, transformers)
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(prepared)) out[k] = serializeCell(v)
    return out
  })

  const headers = Object.keys(serialized[0])
  const rows = [
    headers.map((h) => ({ value: h, fontWeight: 'bold' as const })),
    ...serialized.map((row) => headers.map((h) => ({ value: row[h] ?? '' }))),
  ]
  if (totals) {
    rows.push(headers.map((h) => ({ value: serializeCell(totals[h]), fontWeight: 'bold' as const })))
  }

  const blob = await writeXlsxFile(rows).toBlob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}

export async function parseExcel(
  file: File,
  transformers: Record<string, FieldTransformer> = {}
): Promise<Record<string, unknown>[]> {
  const rows = await readXlsxFile(file)
  if (rows.length < 2) return []

  const headers = rows[0].map((h) => String(h ?? ''))
  return rows.slice(1).map((row) => {
    const obj: Record<string, unknown> = {}
    headers.forEach((h, i) => { obj[h] = deserializeCell(String(row[i] ?? '')) })
    return reverseTransformersOnRow(obj, transformers)
  })
}

/**
 * Validate all rows against field transformers.
 * Returns an array of error strings (empty = no errors).
 */
export function validateRows(
  rows: Record<string, unknown>[],
  transformers: Record<string, FieldTransformer>
): string[] {
  const errors: string[] = []
  rows.forEach((row, i) => {
    for (const [field, tx] of Object.entries(transformers)) {
      if (!tx.validate) continue
      // Run validate on the serialized form (what the user typed in the file)
      // But at this point the value is already deserialized — re-serialize for validation
      const serialized = tx.serialize(row[field])
      const err = tx.validate(serialized)
      if (err) errors.push(`Row ${i + 1}, field "${field}": ${err}`)
    }
  })
  return errors
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function generateId(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 12)
}
